import {
  getMySubscription,
  loadSubscriptionBadgeFromStorage,
  saveSubscriptionBadgeToStorage,
} from '@/services/subscriptions'
import { useState } from 'nuxt/app'
import { computed } from 'vue'

const DAYS_BEFORE_EXPIRY_RED = 5

/** Shared across all useSubscription() callers so badge + page don't double-fetch /me. */
let refreshInflight: Promise<void> | null = null

export type SubscriptionCapability = {
  code: string
  name: string
  type: string
  available: boolean
  limit?: number | null
}

const defaultSubscription = () => ({
  plan: '',
  planCode: '',
  periodStart: '',
  periodEnd: '',
  nextPaymentDate: '',
  nextPaymentAmount: 0,
  isActive: false,
  status: '',
  lastInvoiceId: null as number | null,
})

export function useSubscription() {
  const subscription = useState('subscription', () => {
    const stored = loadSubscriptionBadgeFromStorage()
    if (stored) {
      return {
        plan: stored.plan ?? '',
        planCode: '',
        periodStart: stored.periodStart ?? '',
        periodEnd: stored.periodEnd ?? '',
        nextPaymentDate: stored.nextPaymentDate ?? '',
        nextPaymentAmount: stored.nextPaymentAmount ?? 0,
        isActive: stored.isActive ?? false,
        status: stored.status ?? '',
        lastInvoiceId: stored.lastInvoiceId ?? null,
      }
    }
    return defaultSubscription()
  })
  const subscriptionLoading = useState('subscription-loading', () => false)
  const subscriptionLoaded = useState('subscription-loaded', () => false)
  const capabilities = useState<SubscriptionCapability[]>('subscription-capabilities', () => [])
  const enabledFeatures = useState<string[]>('subscription-enabled-features', () => [])
  const capabilitiesClientId = useState<string | null>('subscription-capabilities-client', () => null)
  const lastMePayload = useState<any>('subscription-last-me-payload', () => null)

  const unwrapData = <T>(payload: T | { data?: T } | null | undefined): T | null => {
    if (!payload) return null
    if (typeof payload === 'object' && payload !== null && 'data' in payload) {
      return (payload as { data?: T }).data ?? null
    }
    return payload as T
  }

  const parseDate = (value?: string | null) => {
    if (!value) return ''
    const dt = new Date(value)
    if (Number.isNaN(dt.getTime())) return ''
    return dt.toISOString().slice(0, 10)
  }

  const usedDays = computed(() => {
    if (!subscription.value.periodStart || !subscription.value.periodEnd) {
      return { used: 0, total: 0 }
    }
    const start = new Date(subscription.value.periodStart)
    const end = new Date(subscription.value.periodEnd)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return { used: 0, total: 0 }
    }
    const now = new Date()
    const startMs = start.getTime()
    const endMs = end.getTime()
    const nowMs = now.getTime()
    const totalMs = endMs - startMs
    const totalDays = Math.ceil(totalMs / (1000 * 60 * 60 * 24))
    const elapsedMs = Math.min(nowMs - startMs, totalMs)
    const days = Math.max(0, Math.ceil(elapsedMs / (1000 * 60 * 60 * 24)))
    return { used: days, total: totalDays }
  })

  const daysLeft = computed(() => usedDays.value.total - usedDays.value.used)
  const isEndingSoon = computed(() => daysLeft.value <= DAYS_BEFORE_EXPIRY_RED && daysLeft.value >= 0)
  const isExpired = computed(() => daysLeft.value < 0)
  const isFree = computed(() => subscription.value.status === 'free')
  const isSubscriptionActive = computed(() =>
    subscription.value.status === 'active'
    || subscription.value.status === 'trialing'
    || subscription.value.status === 'free',
  )

  const badgeColor = computed(() => {
    if (!subscription.value.status) return 'secondary'
    if (subscription.value.status === 'pending_payment') return 'warning'
    if (subscription.value.status === 'trialing') return 'info'
    if (subscription.value.status === 'canceled' || subscription.value.status === 'expired') return 'error'
    return 'success'
  })

  const tooltipText = computed(() => {
    if (subscription.value.status === 'pending_payment') {
      return 'Подписка ожидает оплату счета.'
    }
    if (subscription.value.status === 'trialing') {
      if (isEndingSoon.value) return `Пробный период заканчивается через ${daysLeft.value} дн.`
      return 'Пробный период активен.'
    }
    if (subscription.value.status === 'suspended') {
      return 'Подписка приостановлена.'
    }
    if (!subscription.value.status) return 'Подписка не подключена.'
    if (isExpired.value) return 'Подписка истекла. Продлите для продолжения работы.'
    if (isFree.value) return `Бесплатный тариф.`
    if (isEndingSoon.value) return `Подписка заканчивается через ${daysLeft.value} дн. Продлите вовремя.`
    return 'Подписка активна.'
  })

  function hasCapability(code: string): boolean {
    if (!code) return true
    if (enabledFeatures.value.includes(code)) return true
    const row = capabilities.value.find(c => c.code === code)
    return Boolean(row?.available)
  }

  function syncFromPage(data: {
    plan: string
    periodStart: string
    periodEnd: string
    nextPaymentDate: string
    nextPaymentAmount: number
    status?: string
    lastInvoiceId?: number | null
    planCode?: string
  }) {
    subscription.value = {
      ...subscription.value,
      plan: data.plan,
      periodStart: data.periodStart,
      periodEnd: data.periodEnd,
      nextPaymentDate: data.nextPaymentDate,
      nextPaymentAmount: data.nextPaymentAmount,
      ...(data.status !== undefined && { status: data.status }),
      ...(data.lastInvoiceId !== undefined && { lastInvoiceId: data.lastInvoiceId }),
      ...(data.planCode !== undefined && { planCode: data.planCode }),
    }
  }

  function applyPayload(data: any, clientId?: string | null) {
    const sub = data.subscription ?? data
    const lastInvoice = data.last_invoice
    const planName = sub?.plan?.name ?? subscription.value.plan
    const planCode = sub?.plan?.code ?? subscription.value.planCode
    const start = parseDate(sub?.started_at ?? sub?.created_at) || subscription.value.periodStart
    const end = parseDate(sub?.ends_at) || subscription.value.periodEnd
    const invoiceAmount = Number(lastInvoice?.amount ?? lastInvoice?.total ?? 0)

    const caps: SubscriptionCapability[] = Array.isArray(data.capabilities)
      ? data.capabilities.map((c: any) => ({
          code: String(c.code || ''),
          name: String(c.name || c.code || ''),
          type: String(c.type || 'feature'),
          available: Boolean(c.available),
          limit: c.limit ?? null,
        }))
      : []

    let features: string[] = Array.isArray(data.enabled_features)
      ? data.enabled_features.map((c: any) => String(c))
      : caps.filter(c => c.type === 'feature' && c.available).map(c => c.code)

    // Fallback: derive from subscription items if BE payload is older.
    if (!features.length && Array.isArray(sub?.items)) {
      features = sub.items
        .map((item: any) => item?.capability?.code)
        .filter((code: unknown): code is string => typeof code === 'string' && code.length > 0)
    }

    subscription.value = {
      ...subscription.value,
      plan: planName,
      planCode: planCode || '',
      periodStart: start,
      periodEnd: end,
      nextPaymentDate: end,
      nextPaymentAmount: Number.isFinite(invoiceAmount) ? invoiceAmount : subscription.value.nextPaymentAmount,
      isActive: sub?.status === 'active' || sub?.status === 'trialing',
      status: sub?.status || subscription.value.status,
      lastInvoiceId: lastInvoice?.id ?? null,
    }
    capabilities.value = caps
    enabledFeatures.value = features
    lastMePayload.value = data
    if (clientId != null)
      capabilitiesClientId.value = String(clientId)
    subscriptionLoaded.value = true
    saveSubscriptionBadgeToStorage(data)
  }

  async function refreshFromApi(force = false) {
    if (subscriptionLoaded.value && !force)
      return
    if (refreshInflight)
      return refreshInflight

    refreshInflight = (async () => {
      if (subscriptionLoading.value && !force)
        return
      subscriptionLoading.value = true
      try {
        const response = await getMySubscription()
        const data = unwrapData<any>(response.data.value)

        if (response.error.value || !data)
          return

        const clientId = process.client ? localStorage.getItem('current_client_id') : null
        applyPayload(data, clientId)
      }
      catch (error) {
        console.error('Не удалось загрузить подписку', error)
      }
      finally {
        subscriptionLoading.value = false
      }
    })().finally(() => {
      refreshInflight = null
    })

    return refreshInflight
  }

  async function ensureLoaded(force = false) {
    const clientId = process.client ? localStorage.getItem('current_client_id') : null
    const clientChanged = clientId != null && capabilitiesClientId.value != null
      && String(clientId) !== String(capabilitiesClientId.value)

    if (force || clientChanged || !subscriptionLoaded.value)
      await refreshFromApi(true)
  }

  function invalidate() {
    subscriptionLoaded.value = false
    capabilities.value = []
    enabledFeatures.value = []
    capabilitiesClientId.value = null
    lastMePayload.value = null
  }

  function clearSubscription() {
    subscription.value = defaultSubscription()
    invalidate()
    saveSubscriptionBadgeToStorage(null)
  }

  return {
    subscription,
    subscriptionLoading,
    subscriptionLoaded,
    lastMePayload: computed(() => lastMePayload.value),
    capabilities: computed(() => capabilities.value),
    enabledFeatures: computed(() => enabledFeatures.value),
    planName: computed(() => subscription.value.plan),
    planCode: computed(() => subscription.value.planCode),
    usedDays,
    badgeColor,
    tooltipText,
    isEndingSoon,
    isExpired,
    isFree,
    isSubscriptionActive,
    hasCapability,
    syncFromPage,
    refreshFromApi,
    ensureLoaded,
    invalidate,
    clearSubscription,
  }
}
